import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

interface ComplexityPieChartProps {
  data: {
    simple: number;
    complex: number;
    sprint: number;
    unknown: number;
  };
  isLoading?: boolean;
}

const COLORS = {
  simple: "hsl(142, 76%, 36%)",    // Green
  complex: "hsl(48, 96%, 53%)",    // Yellow
  sprint: "hsl(25, 95%, 53%)",     // Orange
  unknown: "hsl(220, 9%, 46%)",    // Grey
};

export function ComplexityPieChart({ data, isLoading = false }: ComplexityPieChartProps) {
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <Skeleton className="h-5 w-48" />
          <Skeleton className="h-4 w-64 mt-1" />
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex items-center justify-center h-full">
            <Skeleton className="h-64 w-64 rounded-full" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const pieData = [
    { name: "Simple", value: data.simple, fill: COLORS.simple },
    { name: "Complex", value: data.complex, fill: COLORS.complex },
    { name: "Sprint", value: data.sprint, fill: COLORS.sprint },
    { name: "Unknown", value: data.unknown, fill: COLORS.unknown },
  ].filter((item) => item.value > 0);

  const total = pieData.reduce((sum, item) => sum + item.value, 0);

  if (total === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Complexity Distribution</CardTitle>
          <CardDescription>Issues by complexity level</CardDescription>
        </CardHeader>
        <CardContent className="h-80">
          <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
            <p className="text-sm">No complexity data available</p>
            <p className="text-xs mt-1">Metrics will appear as issues are classified</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="chart-complexity-distribution">
      <CardHeader>
        <CardTitle>Complexity Distribution</CardTitle>
        <CardDescription>Issues by complexity level</CardDescription>
      </CardHeader>
      <CardContent className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={pieData}
              dataKey="value"
              nameKey="name"
              cx="50%"
              cy="50%"
              innerRadius={60}
              outerRadius={100}
              paddingAngle={2}
              label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
              labelLine={false}
            >
              {pieData.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.fill} />
              ))}
            </Pie>
            <Tooltip
              contentStyle={{
                backgroundColor: "hsl(var(--card))",
                border: "1px solid hsl(var(--border))",
                borderRadius: "0.5rem",
              }}
              formatter={(value: number) => [`${value} issues`, ""]}
            />
            <Legend />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
